import { radioAnatomy } from "@chakra-ui/anatomy";
import {
  createMultiStyleConfigHelpers,
  SystemStyleObject,
} from "@chakra-ui/react";

const { defineMultiStyleConfig } = createMultiStyleConfigHelpers(
  radioAnatomy.keys,
);

const checkControl: SystemStyleObject = {
  my: "3px",
  position: "relative",
  _checked: {
    _before: {
      content: "''",
      position: "absolute",
      top: "-1px",
      left: "-1px",
      width: "16px",
      height: "16px",
      borderRadius: "full",
      background: "transparent",
      bgColor: "var(--mb-radio-check-color)",
      // Using mask instead of background for bgColor to work. See:
      //  https://stackoverflow.com/a/73908889
      maskImage: "url(/img/check.svg)",
      maskRepeat: "no-repeat",
      maskPosition: "center",
      maskSize: "12px",
    },
  },
};

const RadioTheme = defineMultiStyleConfig({
  sizes: {
    md: {
      control: {
        w: "16px",
        h: "16px",
      },
    },
  },
  baseStyle: {
    control: {
      "--mb-radio-check-color": "var(--chakra-colors-primary-600)",
      bg: "white",
      borderColor: "gray.300",
      borderWidth: 1,
      _checked: {
        backgroundColor: "primary.50",
        borderColor: "primary.600",
        color: "primary.600",
        _before: {
          w: "6px",
          h: "6px",
        },
        _disabled: {
          opacity: 0.5,
          backgroundColor: "primary.50",
          borderColor: "primary.600",
          color: "primary.600",
          _hover: {
            "&:not([data-disabled])": {
              backgroundColor: "primary.50",
              borderColor: "primary.600",
              color: "primary.600",
            },
          },
        },
        _hover: {
          "&:not([data-disabled])": {
            backgroundColor: "primary.50",
            borderColor: "primary.600",
          },
        },
      },
      _indeterminate: {
        backgroundColor: "primary.50",
        borderColor: "primary.600",
        color: "primary.600",
        _disabled: {
          backgroundColor: "gray.100",
          color: "gray.200",
        },
      },
      _disabled: {
        opacity: 0.5,
        backgroundColor: "unset",
        borderColor: "gray.300",
        color: "primary.600",
        _hover: {
          "&:not([data-disabled])": {
            backgroundColor: "unset",
            borderColor: "gray.300",
          },
        },
      },
      _invalid: {
        "--mb-radio-check-color": "var(--chakra-colors-error-600)",
        color: "error.600",
        backgroundColor: "unset",
        borderColor: "error.300",
        _disabled: {
          backgroundColor: "unset",
        },
        _hover: {
          "&:not([data-disabled])": {
            borderColor: "unset",
            backgroundColor: "error.50",
          },
        },
        _checked: {
          color: "error.600",
          borderColor: "unset",
          backgroundColor: "error.50",
        },
        _indeterminate: {
          color: "error.600",
          borderColor: "unset",
          backgroundColor: "error.50",
        },
      },
      _focusVisible: {
        boxShadow: "none",
        outlineStyle: "solid",
        outlineColor: "#F4EBFF",
        outlineWidth: "4px",
      },
      _hover: {
        "&:not([data-disabled])": {
          backgroundColor: "primary.50",
          borderColor: "primary.600",
        },
      },
    },
    label: {
      _invalid: {
        color: "error.600",
      },
    },
  },
  variants: {
    card: {
      label: {
        ml: "16px",
        gap: "16px",
        display: "flex",
        flexDirection: "column",
        fontSize: "unset",
      },
      control: checkControl,
      container: {
        bg: "white",
        borderRadius: "8px",
        borderWidth: "1px",
        borderColor: "gray.200",
        color: "gray.900",
        px: "24px",
        py: "16px",
        width: "100%",
        alignItems: "flex-start",
        _focusWithin: {
          boxShadow: "outline",
          "& .chakra-radio__control": {
            boxShadow: "none",
          },
        },
        "&:not([data-checked]):not([data-disabled]):active": {
          bg: "gray.25",
        },
        _checked: {
          borderColor: "primary.400",
          bg: "primary.50",
          color: "primary.600",
          ".chakra-stat__label": {
            color: "inherit",
          },
        },
        _notLast: {
          mb: "16px",
        },
        "& .chakra-stat__group": {
          gap: "64px",
        },
      },
    },
    check: {
      control: checkControl,
    },
  },
});

export default RadioTheme;
